import { Link, useLocation } from "react-router-dom";

import { useIsDraftTournament } from "../lib/draftTournamentContext";
import Icon from "./icons";

interface TournamentBottomNavProps {
  tournamentId: number | string;
}

export default function TournamentBottomNav({
  tournamentId,
}: TournamentBottomNavProps) {
  const location = useLocation();
  const draft = useIsDraftTournament();
  const base = `/admin/tournaments/${tournamentId}`;
  const path = location.pathname;

  const isExact = (target: string) => path === target;
  const startsWith = (target: string) =>
    path === target || path.startsWith(`${target}/`);

  const activeHome = isExact(base);
  const activeConfig = startsWith(`${base}/settings`);
  const activePlayers = startsWith(`${base}/players`);
  const activeRegistrations = startsWith(`${base}/registrations`);
  const activeGroups = startsWith(`${base}/groups`);
  const activeRounds = startsWith(`${base}/rounds`);
  const activeSchedule = startsWith(`${base}/schedule`);
  const activeStandings = startsWith(`${base}/standings`);
  const activeCommunications = startsWith(`${base}/communications`);
  const activeRules = startsWith(`${base}/rules`);
  const activeResults = startsWith(`${base}/results`);

  const moreActive =
    activeHome ||
    activeConfig ||
    activeCommunications ||
    activeRules ||
    activeResults ||
    activeRounds;

  const divisionId = new URLSearchParams(location.search).get("division");
  const divisionQuery = divisionId ? `?division=${encodeURIComponent(divisionId)}` : "";
  const navTo = (target: string) => `${target}${divisionQuery}`;

  const linkClass = (active: boolean) =>
    active ? "tournament-bottom-nav__link is-active" : "tournament-bottom-nav__link";

  // Torneo en borrador (revisión 2026-10-02): es la barra de la operación del
  // torneo — mientras no se publique, el asistente es la única navegación
  // (igual que el dock «Operación del torneo»).
  if (draft) return null;

  return (
    <nav className="tournament-bottom-nav" aria-label="Acciones del torneo">
      <Link to={navTo(`${base}/players`)} className={linkClass(activePlayers)}>
        <span aria-hidden="true"><Icon name="users" /></span>
        <small>Jugadores</small>
      </Link>
      <Link to={navTo(`${base}/registrations`)} className={linkClass(activeRegistrations)}>
        <span aria-hidden="true"><Icon name="card" /></span>
        <small>Pagos</small>
      </Link>
      <Link to={navTo(`${base}/groups`)} className={linkClass(activeGroups)}>
        <span aria-hidden="true"><Icon name="grid" /></span>
        <small>Grupos</small>
      </Link>
      <Link to={navTo(`${base}/schedule`)} className={linkClass(activeSchedule)}>
        <span aria-hidden="true"><Icon name="calendar" /></span>
        <small>Calendario</small>
      </Link>
      <details
        className={
          moreActive || activeStandings
            ? "tournament-bottom-nav__more is-active"
            : "tournament-bottom-nav__more"
        }
      >
        <summary aria-label="Más secciones del torneo">
          <span aria-hidden="true"><Icon name="more" /></span>
          <small>Más</small>
        </summary>
        <div className="tournament-bottom-nav__menu">
          <Link to={navTo(base)} className={activeHome ? "is-active" : undefined}>
            <span aria-hidden="true"><Icon name="home" /></span>
            Resumen
          </Link>
          <Link to={navTo(`${base}/standings`)} className={activeStandings ? "is-active" : undefined}>
            <span aria-hidden="true"><Icon name="chart" /></span>
            Ranking
          </Link>
          <Link to={navTo(`${base}/settings`)} className={activeConfig ? "is-active" : undefined}>
            <span aria-hidden="true"><Icon name="settings" /></span>
            Configuración
          </Link>
          <Link
            to={navTo(`${base}/rounds`)}
            className={activeRounds ? "is-active" : undefined}
          >
            <span aria-hidden="true"><Icon name="bracket" /></span>
            Draw
          </Link>
          <Link
            to={navTo(`${base}/communications`)}
            className={activeCommunications ? "is-active" : undefined}
          >
            <span aria-hidden="true"><Icon name="mail" /></span>
            Comunicados
          </Link>
          <Link
            to={navTo(`${base}/rules`)}
            className={activeRules ? "is-active" : undefined}
          >
            <span aria-hidden="true"><Icon name="book" /></span>
            Reglamento
          </Link>
          <Link
            to={navTo(`${base}/results`)}
            className={activeResults ? "is-active" : undefined}
          >
            <span aria-hidden="true"><Icon name="check" /></span>
            Resultados
          </Link>
        </div>
      </details>
    </nav>
  );
}
